import React from "react";
import { gql } from "@apollo/client";

import {
  styled,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
} from "@mui/material";
import { PackageDown as InsertIcon } from "mdi-material-ui";

import InventoryBoxEntry from "./InventoryBoxEntry";

const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  "& td:first-of-type, & th:first-of-type": {
    width: 64,
    padding: "6px 16px",
  },
  "& th": { padding: "6px 16px" },

  // Remove last row divider
  "& tbody tr:last-child td": { borderBottom: "none" },

  // Vertical dividers
  "& th:first-of-type": { fontWeight: 700 },
  "& td:not(:first-of-type), & th:not(:first-of-type)": {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  // Hover
  overflow: "hidden",
  position: "relative",
  zIndex: 0,
  "& tr:hover": { backgroundColor: theme.palette.background.odd },
  "& td, & th": { position: "relative" },
  "& td:hover::after, & th:hover::after": {
    content: "''",
    position: "absolute",
    backgroundColor: theme.palette.background.odd,
    left: 0,
    top: -5000,
    height: 10000,
    width: "100%",
    zIndex: -1,
  },
}));

export default function InventoryBox({ box, onInsert, filterId }) {
  const getEntry = (row, column) =>
    box.entries.find((entry) => entry.row === row && entry.column === column);

  return (
    <TableContainer component={Paper} sx={{ "& ~ &": { mt: 4 } }}>
      <StyledTable size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">{box.id}</TableCell>

            {box.columns.map((column) => (
              <TableCell key={column} align="center" width="calc(100% / 10)">
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {box.rows.map((row) => (
            <TableRow key={row}>
              <TableCell align="center">{row}</TableCell>

              {box.columns.map((column, idx) => {
                const entry = getEntry(row, column);

                const filterOut =
                  !!filterId && !entry?.sku.match(new RegExp(filterId));

                return (
                  <TableCell
                    key={idx}
                    padding="none"
                    sx={{ opacity: filterOut ? 0.2 : 1 }}
                  >
                    {entry ? (
                      <InventoryBoxEntry entry={entry} />
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() => onInsert?.({ box, row, column })}
                          sx={{
                            transition: (t) => t.transitions.create("opacity"),
                            opacity: 0,
                            "*:hover > &": { opacity: 1 },
                          }}
                        >
                          <InsertIcon />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
}

InventoryBox.fragments = {
  inventory: gql`
    fragment InventoryBox_inventory on Inventory {
      id
      row
      column

      sku

      ...InventoryBoxEntry_inventory
    }

    ${InventoryBoxEntry.fragments.inventory}
  `,
};
